import {Injectable} from '@angular/core';
import {NotificationRestService} from "../../api/services/notification.rest.service";
import {BehaviorSubject, EMPTY, Observable, of} from "rxjs";
import {NotificationInterface} from "../../shared/interfaces/notification.interface";
import {finalize, map, mergeMap, shareReplay, tap} from "rxjs/operators";
import {ResultListApiModel} from "../../api/models/result-list.api.model";
import {CurrentUserViewModel} from "../models/current-user.view-model";
import {NotificationApiModel} from "../../api/models/notification.api-model";
import {QueryParamsApiModel} from "../../api/models/query-params-api.model";
import {EventSourceService} from "./event-source.service";
import {EventSourceInterface} from "../interfaces/event-source.interface";
import {TokenDataProviderService} from "./token-data-provider.service";
import {MediaObjectRestService} from "../../api/services/media-object.rest.service";
import {FilterParameterApiModel} from "../../api/models/filter-parameter.api.model";
import {MediaObjectApiModel} from "../../api/models/media-object.api-model";

@Injectable({
  providedIn: 'root'
})
export class NotificationDataProviderService {

  public notifications: ResultListApiModel<NotificationInterface> = new ResultListApiModel<NotificationInterface>(NotificationApiModel);
  private inFlightCollections = new Map<string, Observable<ResultListApiModel<NotificationInterface>>>();
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public readonly unreadCount$ = this.unreadCountSubject.asObservable();
  private unreadCountRequest$: Observable<number> | null = null;

  constructor(
    private readonly notificationRS: NotificationRestService,
    private readonly eventSourceService: EventSourceService,
    private readonly tokenDataProviderService: TokenDataProviderService,
    private readonly mediaObjectRS: MediaObjectRestService
  ) {
  }


  public getCollection(page: number = 1, limit: number | null = null): Observable<ResultListApiModel<NotificationInterface>> {
    if (!this.tokenDataProviderService.isLoggedIn()) {
      return of(this.notifications);
    }
    const key = `${page}:${limit ?? 'default'}`;
    const inFlight = this.inFlightCollections.get(key);
    if (inFlight) {
      return inFlight;
    }
    const queryParams = new QueryParamsApiModel();
    queryParams.page = page;
    if (limit !== null) {
      queryParams.limit = limit;
    }
    const request$ = this.notificationRS.getCollection(queryParams).pipe(
      mergeMap((resultListAM: ResultListApiModel<NotificationInterface>) =>
        this.hydrateFromUserAvatars(resultListAM).pipe(
          map((hydrated) => {
            resultListAM.records = hydrated;
            return resultListAM;
          })
        )
      ),
      map((resultListAM: ResultListApiModel<NotificationInterface>) => {
        if (page === 1) {
          this.notifications = resultListAM;
        } else {
          this.notifications.total = resultListAM.total;
        }
        return resultListAM;
      }),
      finalize(() => {
        this.inFlightCollections.delete(key);
      }),
      shareReplay(1)
    );
    this.inFlightCollections.set(key, request$);
    return request$;
  }

  public massUpdateReadAt(notifications: NotificationInterface[]): Observable<ResultListApiModel<NotificationInterface>> {
    if (!this.tokenDataProviderService.isLoggedIn()) {
      return of(this.notifications);
    }
    const unreadMarkedCount = notifications.filter((notification) => notification?.readAt === null).length;
    return this.notificationRS.massUpdateReadAt({notifications: notifications.map(e => e.id), readAt: true}).pipe(map((resultListAM:ResultListApiModel<NotificationInterface>) => {
      this.notifications = resultListAM;
      if (unreadMarkedCount > 0) {
        this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - unreadMarkedCount));
      }
      return this.notifications;
    }));
  }

  public getUnreadCount(): Observable<number> {
    if (!this.tokenDataProviderService.isLoggedIn()) {
      this.unreadCountSubject.next(0);
      return of(0);
    }

    if (this.unreadCountRequest$) {
      return this.unreadCountRequest$;
    }

    this.unreadCountRequest$ = this.notificationRS.getUnreadCount().pipe(
      tap((count) => this.unreadCountSubject.next(count)),
      finalize(() => {
        this.unreadCountRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.unreadCountRequest$;
  }

  public setEventSource(user: CurrentUserViewModel): Observable<EventSourceInterface<NotificationInterface>> {
    if (!this.tokenDataProviderService.isLoggedIn()) {
      return EMPTY;
    }
    return this.eventSourceService.listenSingleTopic<NotificationInterface>(`/users/${user.id}/notifications`, false).pipe(tap((es: EventSourceInterface<NotificationInterface>) => {
      this.applyEvent(es);
    }));
  }

  private applyEvent(es: EventSourceInterface<NotificationInterface>): void {
    const record = es.payload;
    const records = this.notifications.records || [];
    switch (es.state) {
      case 'persisted':
        if (!records.find((item) => item.id === record.id)) {
          this.notifications.records = [record, ...records];
          this.notifications.total++;
          if (record?.readAt === null) {
            this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
          }
        }
        break;
      case 'removed':
        this.notifications.records = records.filter((item) => item.id !== record.id);
        this.notifications.total = Math.max(0, this.notifications.total - 1);
        if (record?.readAt === null) {
          this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - 1));
        }
        break;
    }
  }

  private hydrateFromUserAvatars(recordsList: ResultListApiModel<NotificationInterface>): Observable<NotificationInterface[]> {
    const records = recordsList.records || [];
    const avatarRefs = records
      .map((notification) => notification?.fromUser?.avatar)
      .filter((avatar) => !!avatar);

    const avatarIds = Array.from(new Set(avatarRefs
      .map((avatar: any) => this.resolveMediaId(avatar))
      .filter((id) => !!id)));

    if (!avatarIds.length) {
      return of(records);
    }

    const params = new QueryParamsApiModel();
    params.filters = avatarIds.map((id) => new FilterParameterApiModel('id', id));

    return this.mediaObjectRS.getMediaObjectCollection(params).pipe(
      map((mediaResult: ResultListApiModel<MediaObjectApiModel>) => {
        const mediaById = new Map<string, MediaObjectApiModel>();
        (mediaResult.records || []).forEach((media) => {
          const byId = media?.id ? String(media.id) : null;
          const byIri = media?.['@id'] ? String(media['@id']) : null;
          if (byId) {
            mediaById.set(byId, media);
          }
          if (byIri) {
            mediaById.set(byIri, media);
          }
        });

        return records.map((notification) => {
          const avatarRef: any = notification?.fromUser?.avatar;
          const resolved = this.resolveMediaId(avatarRef);
          if (!resolved || !notification?.fromUser) {
            return notification;
          }
          const media = mediaById.get(resolved);
          if (!media) {
            return notification;
          }
          notification.fromUser.avatar = media as any;
          return notification;
        });
      })
    );
  }

  private resolveMediaId(avatar: any): string | null {
    if (!avatar) {
      return null;
    }
    if (typeof avatar === 'string') {
      const parts = avatar.split('/');
      return parts[parts.length - 1] || null;
    }
    if (avatar.id !== undefined && avatar.id !== null) {
      return String(avatar.id);
    }
    if (avatar['@id']) {
      const parts = String(avatar['@id']).split('/');
      return parts[parts.length - 1] || null;
    }
    return null;
  }
}
